// Svelte 5 no longer needs svelte/store imports for basic reactivity

import { projectState } from "./projectStore.svelte.js";
import { isQuickToolsPanelAllowed } from "./permissionStore.svelte.js";

export const QUICK_TOOLS_PANEL_IDS = [
    "summary",
    "styles",
    "findAndReplace",
    "spellCheck",
    "timingAndSync",
    "liveCaptioning",
    "qcAndReview",
    "manualQc",
    "errors",
    "speakers",
    "tags",
    "markers",
    "notes",
    "videoFilters",
    "eventTemplates",
    "voices",
    "media",
];

const QUICK_TOOLS_PANEL_VISIBILITY = {
    summary: "summary",
    styles: "styles",
    findAndReplace: "search",
    spellCheck: "spellCheck",
    timingAndSync: "timing",
    liveCaptioning: "liveCaptioning",
    qcAndReview: "qc",
    manualQc: "manualQc",
    errors: "errorNav",
    speakers: "speakers",
    tags: "tags",
    markers: "markers",
    notes: "notes",
    videoFilters: "videoFilters",
    eventTemplates: "templates",
    voices: "voices",
    media: "media",
};

const QUICK_TOOLS_VISIBILITY_KEYS = [
    "styles",
    "summary",
    "search",
    "spellCheck",
    "timing",
    "liveCaptioning",
    "qc",
    "errorNav",
    "templates",
    "voices",
    "speakers",
    "tags",
    "notes",
    "markers",
    "videoFilters",
    "manualQc",
    "media",
];

function loadUiSettingsFromLocalStorage() {
    let uiSettings = {
        layout: "default", //default, reverse, vertical
        timeline: true,
        listPreview: false,
        knpLookup : false,
        assistant: {
            open: false,
            width: 420,
            historyWidth: 220,
            historyCollapsed: window.innerWidth < 1440,
        },
        quickTools: {
            selected: "summary",
            styles: true,
            summary: true,
            search: true,
            spellCheck: true,
            timing: true,
            liveCaptioning: true,
            qc: true,
            errorNav: true,
            templates: true,
            voices: true,
            speakers: true,
            tags: true,
            notes: true,
            markers: true,
            videoFilters: true,
            manualQc: true,
            media: true
        }
    }

    let settings = JSON.parse(localStorage.getItem("cc-ui-settings")) || {};
    let defaultWorkspace = localStorage.getItem("cc-workspace-default") || 'default';
    uiSettings.layout = defaultWorkspace;
    Object.entries(settings).forEach(entry => {
        if (typeof(entry[1]) === "object") {
            Object.entries(entry[1]).forEach(setting => {
                uiSettings[entry[0]][setting[0]] = setting[1];
            }); 
        } else {
            uiSettings[entry[0]] = entry[1];
        }               
    });

    return uiSettings;
}

// Create reactive state for UI settings
export const uiState = $state(loadUiSettingsFromLocalStorage());

function normalizePanelName(panelName) {
    if (typeof panelName !== "string") {
        return null;
    }

    const trimmed = panelName.trim();
    return (
        QUICK_TOOLS_PANEL_IDS.find(
            (id) => id.toLowerCase() === trimmed.toLowerCase(),
        ) ?? null
    );
}

function isQuickToolsPanelVisible(panelId) {
    const visibilityKey = QUICK_TOOLS_PANEL_VISIBILITY[panelId];
    return visibilityKey
        ? Boolean(uiState.quickTools[visibilityKey]) && isQuickToolsPanelAllowed(panelId)
        : false;
}

function applyQuickToolsPanelMode(panelId) {
    if (panelId === "timingAndSync") {
        projectState.mode = "timing";
    } else {
        projectState.mode = "edit";
    }
}

const QUICK_TOOLS_FALLBACK_ORDER = [
    "summary",
    "findAndReplace",
    "spellCheck",
    "timingAndSync",
    "qcAndReview",
    "speakers",
];

export function selectFallbackQuickToolsPanel() {
    uiState.quickTools.selected =
        QUICK_TOOLS_FALLBACK_ORDER.find(isQuickToolsPanelVisible) ??
        QUICK_TOOLS_PANEL_IDS.find(isQuickToolsPanelVisible) ??
        "styles";

    applyQuickToolsPanelMode(uiState.quickTools.selected);
}

export function selectQuickToolsPanel(panelName) {
    const panelId = normalizePanelName(panelName);

    if (!panelId) {
        return {
            success: false,
            message: `Unknown panel: ${panelName}`,
        };
    }

    if (!isQuickToolsPanelVisible(panelId)) {
        return {
            success: false,
            message: `Panel is not enabled: ${panelId}`,
        };
    }

    uiState.quickTools.selected = panelId;
    applyQuickToolsPanelMode(panelId);

    return {
        success: true,
        selected: panelId,
    };
}

export function setQuickToolsSettings(settings) {
    if (!settings || typeof settings !== "object") {
        return {
            success: false,
            message: "Settings object is required.",
        };
    }

    const { selected, ...visibilitySettings } = settings;

    for (const [key, value] of Object.entries(visibilitySettings)) {
        if (QUICK_TOOLS_VISIBILITY_KEYS.includes(key)) {
            uiState.quickTools[key] = Boolean(value);
        }
    }

    if (selected !== undefined) {
        const result = selectQuickToolsPanel(selected);
        if (!result.success) {
            selectFallbackQuickToolsPanel();
        }
    } else if (!isQuickToolsPanelVisible(uiState.quickTools.selected)) {
        selectFallbackQuickToolsPanel();
    } else {
        applyQuickToolsPanelMode(uiState.quickTools.selected);
    }

    return {
        success: true,
        quickTools: { ...uiState.quickTools },
    };
}

// Function to save UI settings to localStorage
export function saveUiSettings() {
    localStorage.setItem("cc-ui-settings", JSON.stringify(uiState));
}

// Function to update UI layout
export function updateLayout(layout) {
    uiState.layout = layout;
    saveUiSettings();
}

// Function to toggle timeline visibility
export function toggleTimeline() {
    uiState.timeline = !uiState.timeline;
    saveUiSettings();
}

// Function to toggle list preview
export function toggleListPreview() {
    uiState.listPreview = !uiState.listPreview;
    saveUiSettings();
}

// Function to toggle KNP Lookup offcanvas
export function toggleKnpLookup() {
    uiState.knpLookup = !uiState.knpLookup;
}

// Function to toggle the AI Assistant panel
export function toggleAssistant(open = !uiState.assistant.open) {
    uiState.assistant.open = open;
    saveUiSettings();
}
